import React, { ReactNode } from 'react';
import { ListItem, styled, Typography } from '@mui/material';

type ModuleCardTone = 'default' | 'success' | 'error' | 'warning' | 'info' | 'primary';

export type ModuleCardField = {
  label: string;
  value: ReactNode;
  fullWidth?: boolean;
};

export type ModuleCardMetric = {
  label: string;
  value: ReactNode;
  tone?: ModuleCardTone;
};

type ModuleCardProps = {
  idLabel?: ReactNode;
  title: ReactNode;
  subtitle?: ReactNode;
  status?: ReactNode;
  statusTone?: ModuleCardTone;
  actions?: ReactNode;
  fields?: ModuleCardField[];
  metrics?: ModuleCardMetric[];
  onClick?: () => void;
  // card com opacidade reduzida (ex.: registro inativo)
  muted?: boolean;
  // card denso para celular: título + status na mesma linha e métricas em chips numa linha só
  dense?: boolean;
  children?: ReactNode;
};

const ListItemStyled = styled(ListItem)(({ theme }) => ({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  gap: theme.spacing(1.5),
  padding: theme.spacing(1.5),
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.background.paper,
  transition: 'border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease',
  '&.muted': {
    opacity: 0.6,
  },
  '&.dense': {
    gap: theme.spacing(0.75),
    padding: theme.spacing(1.25),
  },
  '&[role="button"]': {
    cursor: 'pointer',
  },
  '&[role="button"]:hover': {
    borderColor: theme.palette.primary.light,
    boxShadow: '0 10px 24px rgba(15, 23, 42, 0.08)',
    transform: 'translateY(-1px)',
  },
}));

const Header = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr) auto',
  gap: theme.spacing(1),
  alignItems: 'flex-start',
}));

const HeaderInfo = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.75),
  minWidth: 0,
}));

const HeaderMeta = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.75),
  minWidth: 0,
  flexWrap: 'wrap',
}));

const IdLabel = styled(Typography)(({ theme }) => ({
  display: 'inline-flex',
  width: 'fit-content',
  maxWidth: '100%',
  padding: '2px 6px',
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.grey[100],
  color: theme.palette.text.secondary,
  fontSize: 12,
  fontWeight: 700,
  lineHeight: 1.2,
}));

const Status = styled('span')<{ tone: ModuleCardTone }>(({ theme, tone }) => {
  const colors = {
    default: {
      backgroundColor: theme.palette.grey[200],
      color: theme.palette.text.primary,
    },
    success: {
      backgroundColor: theme.palette.success.main,
      color: theme.palette.success.contrastText,
    },
    error: {
      backgroundColor: theme.palette.error.main,
      color: theme.palette.error.contrastText,
    },
    warning: {
      backgroundColor: theme.palette.warning.main,
      color: theme.palette.warning.contrastText,
    },
    info: {
      backgroundColor: theme.palette.info.main,
      color: theme.palette.info.contrastText,
    },
    primary: {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
    },
  };

  return {
    display: 'inline-flex',
    alignItems: 'center',
    width: 'fit-content',
    maxWidth: '100%',
    padding: '4px 8px',
    borderRadius: theme.shape.borderRadius,
    fontSize: 12,
    fontWeight: 700,
    lineHeight: 1.2,
    ...colors[tone],
  };
});

const Title = styled(Typography)({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  display: '-webkit-box',
  WebkitLineClamp: 2,
  lineClamp: 2,
  WebkitBoxOrient: 'vertical',
});

const Actions = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: theme.spacing(0.25),
  whiteSpace: 'nowrap',
}));

const Fields = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: theme.spacing(1.25),
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: '1fr',
  },
}));

const Field = styled('div')<{ fullWidth?: boolean }>(({ fullWidth }) => ({
  minWidth: 0,
  gridColumn: fullWidth ? '1 / -1' : undefined,
}));

const Description = styled(Typography)({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  display: '-webkit-box',
  WebkitLineClamp: 1,
  lineClamp: 1,
  WebkitBoxOrient: 'vertical',
});

const Metrics = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
  gap: theme.spacing(1),
  paddingTop: theme.spacing(1),
  borderTop: `1px solid ${theme.palette.divider}`,
  [theme.breakpoints.down('md')]: {
    gridTemplateColumns: '1fr 1fr',
  },
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: '1fr',
  },
}));

const Metric = styled('div')<{ tone: ModuleCardTone }>(({ theme, tone }) => ({
  minWidth: 0,
  padding: theme.spacing(1),
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.grey[50],
  textAlign: 'right',
  '& .metric-value': {
    color:
      tone === 'success'
        ? theme.palette.success.main
        : tone === 'error'
          ? theme.palette.error.main
          : tone === 'primary'
            ? theme.palette.primary.main
            : theme.palette.text.primary,
  },
}));

const DenseHeader = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr) auto',
  gap: theme.spacing(1),
  alignItems: 'flex-start',
}));

const DenseSide = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  '& .status': {
    padding: '2px 6px',
    fontSize: 11,
  },
}));

const DenseMetrics = styled('div')(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(0.375),
}));

const DenseMetric = styled('span')<{ tone: ModuleCardTone }>(({ theme, tone }) => ({
  display: 'inline-flex',
  alignItems: 'baseline',
  gap: 3,
  padding: '1px 6px',
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 999,
  backgroundColor: theme.palette.grey[50],
  fontSize: 11.5,
  lineHeight: 1.4,
  color: theme.palette.text.secondary,
  '& b': {
    fontWeight: 800,
    color: tone === 'primary' ? theme.palette.primary.main : theme.palette.text.primary,
  },
}));

const ModuleCard: React.FC<ModuleCardProps> = ({
  idLabel,
  title,
  subtitle,
  status,
  statusTone = 'default',
  actions,
  fields = [],
  metrics = [],
  onClick,
  muted = false,
  dense = false,
  children,
}) => {
  const className = [muted ? 'muted' : '', dense ? 'dense' : ''].filter(Boolean).join(' ') || undefined;

  if (dense) {
    return (
      <ListItemStyled role={onClick ? 'button' : undefined} onClick={onClick} className={className}>
        <DenseHeader>
          <div style={{ minWidth: 0 }}>
            <Title variant="subtitle2" fontWeight={700}>
              {title}
            </Title>
            {subtitle && (
              <Typography variant="caption" color="textSecondary" noWrap display="block">
                {subtitle}
              </Typography>
            )}
          </div>
          <DenseSide>
            {status && (
              <Status className="status" tone={statusTone}>
                {status}
              </Status>
            )}
            {actions}
          </DenseSide>
        </DenseHeader>

        {metrics.length > 0 && (
          <DenseMetrics>
            {metrics.map(metric => (
              <DenseMetric key={metric.label} tone={metric.tone || 'default'}>
                {metric.label} <b>{metric.value || '-'}</b>
              </DenseMetric>
            ))}
          </DenseMetrics>
        )}

        {children}
      </ListItemStyled>
    );
  }

  return (
    <ListItemStyled role={onClick ? 'button' : undefined} onClick={onClick} className={className}>
      <Header>
        <HeaderInfo>
          <HeaderMeta>
            {idLabel && <IdLabel>{idLabel}</IdLabel>}
            {status && <Status tone={statusTone}>{status}</Status>}
          </HeaderMeta>
          <div>
            <Title variant="subtitle2" fontWeight={700}>
              {title}
            </Title>
            {subtitle && (
              <Typography variant="caption" color="textSecondary">
                {subtitle}
              </Typography>
            )}
          </div>
        </HeaderInfo>

        {actions && <Actions>{actions}</Actions>}
      </Header>

      {fields.length > 0 && (
        <Fields>
          {fields.map(field => (
            <Field key={field.label} fullWidth={field.fullWidth}>
              <Typography variant="caption" color="textSecondary">
                {field.label}
              </Typography>
              <Description variant="body2" fontWeight={600}>
                {field.value || 'Não informado'}
              </Description>
            </Field>
          ))}
        </Fields>
      )}

      {metrics.length > 0 && (
        <Metrics>
          {metrics.map(metric => (
            <Metric key={metric.label} tone={metric.tone || 'default'}>
              <Typography variant="caption" color="textSecondary">
                {metric.label}
              </Typography>
              <Typography className="metric-value" variant="body2" fontWeight={800}>
                {metric.value || 'Não informado'}
              </Typography>
            </Metric>
          ))}
        </Metrics>
      )}

      {children}
    </ListItemStyled>
  );
};

export default ModuleCard;
